import React, { useState, useCallback, useEffect, useRef, createContext } from 'react';
import { Typography, makeStyles, createStyles } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import InputForm from '../common/InputForm';
import ErrorDialog from '../common/ErrorDialog';
import TopBar from '../common/TopBar';
import AlertDialog from '../common/AlertDialog';
import Loading from '../common/Loading';
import { get日付 } from '../../helper/JapanDate';
import { fetchKirokuItem, postKirokuItem } from '../../helper/Api';
import { isIllegalOparation } from '../../helper/Util';
import { errors, getApiError } from '../../helper/Error';
import SaveButton from '../common/SaveButton';

// CSSスタイルを定義
const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'left',
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    subheader: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      top: theme.mixins.toolbar.minHeight,
      background: 'inherit',
      padding: theme.spacing(2),
      textAlign: 'left',
      backgroundColor: theme.palette.background.default,
      zIndex: 500,
    },
    subheaderSticky: {
      position: 'sticky',
    },
    innerTitle: {
      maxWidth: 600,
      width: '100%',
    },
  }),
);

export const SubheaderContext = createContext({
  setIsFocusTextarea: () => {},
});

const Input = React.memo(() => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { selectedDate, selectedName, serviceName, serviceId, kakunin, timeRange } = location.state ?? 0;

  const saveData = useRef([]);
  const handleChangeSaveData = useCallback((rootIdx, childIdx, grandChildIdx, newValue) => {
    if (saveData.current[rootIdx].child[childIdx].child === undefined) {
      saveData.current[rootIdx].child[childIdx].value = newValue;
    } else {
      saveData.current[rootIdx].child[childIdx].child[grandChildIdx].value = newValue;
    }
  }, []);

  const [isOpenResult, setIsOpenResult] = useState(false);

  const handleCloseResult = useCallback(() => {
    setIsOpenResult(false);
    // 詳細画面までブラウザバックする
    history.go(-1);
  }, [history]);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);

  const errorRef = useRef({});
  const [isOpenError, setIsOpenError] = useState(false);
  const handleCloseError = useCallback(() => {
    setIsOpenError(false);

    if (errorRef.current === errors.APP_ILLEGAL_OPERATION) {
      history.push('/calendar');
    }
  }, [history]);

  useEffect(() => {
    const fn = async () => {
      setIsLoading(true);

      await fetchKirokuItem(serviceId)
        .then((res) => {
          if (res.data.error.length === 0) {
            setData(res.data.result);
            saveData.current = res.data.result;
            setIsShowInput(true);
          } else {
            throw res.data.error;
          }
        })
        .catch((err) => {
          setIsShowInput(false);
          errorRef.current = err.code ? getApiError(err) : errors.APP_CONNECTION_FAILED;
          setIsOpenError(true);
        });

      setIsLoading(false);
    };

    if (isIllegalOparation(location)) {
      errorRef.current = errors.APP_ILLEGAL_OPERATION;
      setIsOpenError(true);
    } else {
      fn();
    }
  }, [serviceId, location]);

  const handleSave = useCallback(() => {
    const fn = async () => {
      setIsSaving(true);

      await postKirokuItem(serviceId, saveData.current)
        .then((res) => {
          if (res.data.error.length === 0) {
            setIsOpenResult(true);
          } else throw res.data.error;
        })
        .catch((err) => {
          errorRef.current = err.code ? getApiError(err) : errors.APP_CONNECTION_FAILED;
          setIsOpenError(true);
        });

      setIsSaving(false);
    };
    fn();
  }, [serviceId]);

  const [isShowInput, setIsShowInput] = useState(false);

  const [isFocusTextarea, setIsFocusTextarea] = useState(false);

  return (
    <>
      <TopBar isLoading={isLoading} />

      {isIllegalOparation(location) ? null : (
        <>
          <div className={isFocusTextarea ? classes.subheader : classNames(classes.subheader, classes.subheaderSticky)}>
            <div className={classes.innerTitle}>
              <Typography variant="subtitle1">{selectedName}</Typography>
              <Typography variant="subtitle2" color="textSecondary">
                {`${get日付(selectedDate)} ${timeRange}`}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                {serviceName}
              </Typography>
            </div>
          </div>

          <div className={classes.root}>
            {isShowInput ? (
              <>
                <SubheaderContext.Provider value={{ setIsFocusTextarea }}>
                  <InputForm inputData={data} onChange={handleChangeSaveData} />
                </SubheaderContext.Provider>

                <SaveButton handleSave={handleSave} kakunin={kakunin} />
              </>
            ) : null}
          </div>
        </>
      )}

      <AlertDialog open={isOpenResult} onClose={handleCloseResult}>
        正常に保存されました
      </AlertDialog>

      <ErrorDialog open={isOpenError} onClose={handleCloseError} error={errorRef.current} />

      <Loading open={isSaving} />
    </>
  );
});

export default Input;
